/**
 * SocialMediaLinksSection Component
 *
 * Component for displaying and managing social media links
 * Based on Figma design: node-id=1-1448
 */

'use client';

import { Typography } from '@/src/shared/ui/typography/Typography';
import { Icon } from '@/src/shared/ui/icons/Icon';
import { SecondaryButton } from '@/src/shared/ui/button';

export type SocialMediaPlatform =
  | 'instagram'
  | 'facebook'
  | 'twitter'
  | 'linkedin'
  | 'youtube'
  | 'tiktok'
  | 'website';

export interface SocialMediaLink {
  id: string;
  platform: SocialMediaPlatform;
  url: string;
}

export interface SocialMediaLinksSectionProps {
  socialMediaLinks: SocialMediaLink[];
  onSocialMediaLinksChange: (links: SocialMediaLink[]) => void;
  onAddSocialMedia: () => void;
  disabled?: boolean;
}

const PLATFORM_OPTIONS: { value: SocialMediaPlatform; label: string }[] = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'twitter', label: 'Twitter/X' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'website', label: 'Website' },
];

export function SocialMediaLinksSection({
  socialMediaLinks,
  onSocialMediaLinksChange,
  onAddSocialMedia,
  disabled = false,
}: SocialMediaLinksSectionProps) {
  const handleLinkChange = (
    index: number,
    field: keyof SocialMediaLink,
    value: string
  ) => {
    const newLinks = [...socialMediaLinks];
    newLinks[index] = { ...newLinks[index], [field]: value };
    onSocialMediaLinksChange(newLinks);
  };

  const handleRemoveLink = (index: number) => {
    const newLinks = socialMediaLinks.filter((_, i) => i !== index);
    onSocialMediaLinksChange(newLinks);
  };

  return (
    <div>
      <Typography variant="body" weight="medium" className="mb-4">
        Social media links
      </Typography>

      <div className="space-y-4">
        {socialMediaLinks.map((link, index) => (
          <div key={link.id} className="flex items-center gap-3">
            {/* Platform Select */}
            <div className="w-40 relative">
              <select
                value={link.platform}
                onChange={(e) =>
                  handleLinkChange(index, 'platform', e.target.value as SocialMediaPlatform)
                }
                disabled={disabled}
                className="w-full px-4 py-2.5 border border-border-primary rounded-xl bg-white
                  text-text-primary appearance-none cursor-pointer
                  focus:outline-none focus:border-accent-primary focus:ring-1 focus:ring-accent-primary
                  disabled:bg-background-secondary disabled:text-text-disabled disabled:cursor-not-allowed
                  transition-colors pr-10"
              >
                {PLATFORM_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <Icon name="arrow-down" size="sm" color="secondary" />
              </div>
            </div>

            {/* URL Input */}
            <div className="flex-1">
              <input
                type="url"
                value={link.url}
                onChange={(e) => handleLinkChange(index, 'url', e.target.value)}
                placeholder="Paste the link"
                disabled={disabled}
                className="w-full px-4 py-2.5 border border-border-primary rounded-xl bg-white
                  text-text-primary placeholder:text-text-tertiary
                  focus:outline-none focus:border-accent-primary focus:ring-1 focus:ring-accent-primary
                  disabled:bg-background-secondary disabled:text-text-disabled disabled:cursor-not-allowed
                  transition-colors"
              />
            </div>

            {/* Remove Button */}
            {socialMediaLinks.length > 1 && (
              <button
                type="button"
                onClick={() => handleRemoveLink(index)}
                disabled={disabled}
                className="p-2 hover:bg-background-secondary rounded-full transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon name="x" size="sm" color="secondary" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Add Social Media Button */}
      <div className="flex justify-center mt-4">
        <SecondaryButton
          onClick={onAddSocialMedia}
          disabled={disabled}
          className="border-accent-primary text-accent-primary hover:bg-accent-primary/5"
        >
          <Icon name="plus" size="sm" className="mr-2" />
          Add Social Media
        </SecondaryButton>
      </div>
    </div>
  );
}
